import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../shared/auth";
import { isAdmin, leaderboardHumansOnly, setLeaderboardHumansOnly } from "../../shared/admin";
import { useModalHeadSlot } from "../../shared/modal/Modal";
import { useSearchParams } from "react-router-dom";
import { fetchLeaderboard, fetchLeaderboardAround, fetchRankedLeaderboard, fetchRankedLeaderboardAround, type LeaderboardEntry, type RankedMode } from "../../shared/rating/store";
import SectionTabs from "../../shared/section-tabs/SectionTabs";
import RankInsignia from "../../shared/rating/RankInsignia";
import { flagUrlSquare, isFlagCode, flagName } from "../../shared/flags";
import { ladderSteps } from "../../shared/rating/tiers";
import { fetchRoyaleLeaders, type RoyaleLeader } from "../../shared/royale";
import { useLandscapePhone } from "../../shared/useResponsive";
import styles from "./LeaderboardPage.module.scss";

// Medals for the podium; everyone else shows their numeric rank.
const MEDALS = ["🥇", "🥈", "🥉"];

const TOP_N = 100;

// Four ladders, four tabs: the two ranked modes, the Royale's winners, then
// the casual one (the open boxes, "for fun"). The tab lives in the URL
// (?tab=ffa) like the profile's, so each ladder is its own address.
type LadderTab = "duel" | "ffa" | "royale" | "casual";
const LADDERS: { id: LadderTab; label: string }[] = [
  { id: "duel", label: "1 vs 1" },
  { id: "ffa", label: "6 FFA" },
  { id: "royale", label: "Royale wins" },
  { id: "casual", label: "Casual" },
];
// The admin's "real players" filter drops the bots from every ladder they are
// listed on as players (casual and both ranked ones).
type Filter = Parameters<typeof fetchLeaderboard>[1];
const fetchTop = (tab: LadderTab, n: number, filter: Filter) => (tab === "casual" ? fetchLeaderboard(n, filter) : fetchRankedLeaderboard(tab as RankedMode, n, filter));
const fetchAround = (tab: LadderTab, id: string, span: number, filter: Filter) => (tab === "casual" ? fetchLeaderboardAround(id, span, filter) : fetchRankedLeaderboardAround(tab as RankedMode, id, span, filter));
// Rows shown either side of the signed-in player when they sit below the top.
const AROUND_SPAN = 5;
// Body rows in the loading skeleton: about a panel's worth.
const SKELETON_ROWS = 14;

// A break in the table between the top of the ladder and the signed-in
// player's neighbourhood further down.
type GapRow = { gap: number };
type Row = LeaderboardEntry | GapRow;
const isGap = (r: Row): r is GapRow => "gap" in r;

// Loading state: the real header over a panel of shimmering placeholder rows,
// so the table's shape is there from the first frame and the data just fills
// it in. Name bars vary in width so it reads as a list, not a grid of bars.
function SkeletonRows() {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <tr key={i} className={styles.lbSkelRow} aria-hidden="true">
          <td className={styles.lbRank}><span className={styles.lbSkelBar} style={{ width: 18 }} /></td>
          <td className={styles.lbFlag} />
          <td className={styles.lbName}><span className={styles.lbSkelBar} style={{ width: `${44 + ((i * 37) % 40)}%` }} /></td>
          <td className={styles.lbBadge}><span className={`${styles.lbSkelBar} ${styles.lbSkelDot}`} /></td>
          <td className={`${styles.lbNum} ${styles.lbEloValue}`}><span className={styles.lbSkelBar} style={{ width: 40 }} /></td>
          <td className={`${styles.lbNum} ${styles.lbGames} ${styles.lbHideSm}`}><span className={styles.lbSkelBar} style={{ width: 26 }} /></td>
          <td className={`${styles.lbNum} ${styles.lbPct}`}><span className={styles.lbSkelBar} style={{ width: 34 }} /></td>
        </tr>
      ))}
    </>
  );
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return n + "th";
  return n + (["th", "st", "nd", "rd"][n % 10 < 4 ? n % 10 : 0] || "th");
}

// The Royale's ladder: everyone who has won one, most wins first. It is a
// count, not a rating, so it has its own columns (wins, played, best placing).
// A read that fails comes back empty (shared/royale.ts), so the only states
// are loading, nobody yet, and the list.
function RoyaleLadder({ tableRef, meRef }: { tableRef: React.RefObject<HTMLTableElement>; meRef: React.RefObject<HTMLTableRowElement> }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<RoyaleLeader[] | undefined>(undefined);
  useEffect(() => {
    let live = true;
    fetchRoyaleLeaders(TOP_N).then((r) => { if (live) setRows(r); });
    return () => { live = false; };
  }, []);
  if (rows && rows.length === 0) return <div className="section-placeholder">No Royale has been won yet</div>;
  return (
    <table className={`${styles.lbTable} ${styles.lbRoyale}`} ref={tableRef} aria-busy={rows ? undefined : true} aria-label={rows ? undefined : "Loading the leaderboard"}>
      <thead>
        <tr>
          <th className={styles.lbRank}>#</th>
          <th className={styles.lbFlag} aria-label="Flag" />
          <th>Player</th>
          <th className={`${styles.lbNum} ${styles.lbRoyWins}`}>Wins</th>
          <th className={`${styles.lbNum} ${styles.lbRoyNum}`}>Played</th>
          <th className={`${styles.lbNum} ${styles.lbRoyNum}`}>Best</th>
        </tr>
      </thead>
      <tbody>
        {!rows ? Array.from({ length: SKELETON_ROWS }, (_, i) => (
          <tr key={i} className={styles.lbSkelRow} aria-hidden="true">
            <td className={styles.lbRank}><span className={styles.lbSkelBar} style={{ width: 18 }} /></td>
            <td className={styles.lbFlag} />
            <td className={styles.lbName}><span className={styles.lbSkelBar} style={{ width: `${44 + ((i * 37) % 40)}%` }} /></td>
            <td className={`${styles.lbNum} ${styles.lbRoyWins}`}><span className={styles.lbSkelBar} style={{ width: 22 }} /></td>
            <td className={`${styles.lbNum} ${styles.lbRoyNum}`}><span className={styles.lbSkelBar} style={{ width: 26 }} /></td>
            <td className={`${styles.lbNum} ${styles.lbRoyNum}`}><span className={styles.lbSkelBar} style={{ width: 26 }} /></td>
          </tr>
        )) : rows.map((r, i) => {
          const isMe = !!user && r.userId === user.id;
          const profilePath = isMe ? "/profile" : `/profile/${encodeURIComponent(r.userId)}`;
          return (
            <tr
              key={r.userId}
              className={isMe ? `${styles.lbRow} ${styles.lbMe}` : styles.lbRow}
              ref={isMe ? meRef : undefined}
              aria-label={isMe ? "You" : undefined}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("a")) return;
                navigate(profilePath);
              }}
            >
              <td className={styles.lbRank}>{i < 3 ? <span className={styles.lbMedal}>{MEDALS[i]}</span> : i + 1}</td>
              <td className={styles.lbFlag}>
                {r.flag && isFlagCode(r.flag) ? <img className={styles.lbFlagImg} src={flagUrlSquare(r.flag)} alt="" title={flagName(r.flag)} loading="lazy" /> : null}
              </td>
              <td className={styles.lbName}>
                <Link to={profilePath} className={styles.lbNameLink} title={isMe ? "Your profile" : `${r.name}'s profile`}>{r.name}</Link>
              </td>
              <td className={`${styles.lbNum} ${styles.lbRoyWins}`}>{r.wins}</td>
              <td className={`${styles.lbNum} ${styles.lbRoyNum}`}>{r.played}</td>
              <td className={`${styles.lbNum} ${styles.lbRoyNum}`}>{r.bestPlace ? ordinal(r.bestPlace) : "–"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Global casual leaderboard: everyone with at least one finished public match,
// ranked by rating, read live from Supabase. Three distinct states: loading,
// failed-to-load (backend missing/erroring), and genuinely empty; a load
// failure must never masquerade as "no ranked players yet".
//
// The signed-in player always finds themselves: if they rank below the top
// TOP_N, their neighbourhood (AROUND_SPAN rows either side) is appended after a
// gap row, and on load the table scrolls so their row sits mid-panel.
//
// Every row opens that player's profile (their own for the signed-in player,
// the public overview for anyone else).
//
// Admins get a "real players" switch in the header that drops the bots from
// the list (the public board lists them as ordinary players; see
// LeaderboardFilter in rating/store.ts). Positions stay the public ones.
export default function LeaderboardPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<LadderTab>(() => {
    const t = params.get("tab");
    return (LADDERS.some((x) => x.id === t) ? t : "duel") as LadderTab;
  });
  const selectTab = (t: LadderTab) => {
    setTab(t);
    setParams(t === "duel" ? {} : { tab: t }, { replace: true });
    centeredRef.current = false;
  };
  const navigate = useNavigate();
  // A landscape phone: the page is a full-screen table that scrolls inside
  // its own frame (see .lb-page in the stylesheet).
  const app = useLandscapePhone();
  const pageRef = useRef<HTMLDivElement>(null);
  const royale = tab === "royale";
  const admin = isAdmin(user);
  const [humansOnly, setHumansOnly] = useState(leaderboardHumansOnly);
  const filter = admin && humansOnly ? { humansOnly: true } : {};
  const filterKey = admin && humansOnly ? "humans" : "all";
  // undefined = loading, null = load failed, [] = empty, rows = data.
  const [fetched, setFetched] = useState<LeaderboardEntry[] | null | undefined>(undefined);
  // The signed-in player's neighbourhood; undefined while loading (or not
  // needed), null = failed, [] = no row of their own.
  const [around, setAround] = useState<LeaderboardEntry[] | null | undefined>(undefined);

  useEffect(() => {
    if (royale) return; // the Royale tab reads its own list (RoyaleLadder)
    let live = true;
    setFetched(undefined);
    fetchTop(tab, TOP_N, filter).then((data) => {
      if (live) setFetched(data);
    });
    return () => {
      live = false;
    };
  }, [tab, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user || royale) { setAround([]); return; }
    let live = true;
    setAround(undefined);
    fetchAround(tab, user.id, AROUND_SPAN, filter).then((data) => {
      if (live) setAround(data);
    });
    return () => {
      live = false;
    };
  }, [user?.id, tab, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once the player's own row is on screen, centre it in the scroll panel.
  // Once per load: later re-renders (tooltips, auth refresh) must not yank
  // the list back while the player is scrolling around.
  // Only when the row is actually out of view: a short board that already
  // shows it (between the column header and the bottom of the scroller) stays
  // put, or centring would slide the podium up under the sticky headers.
  const meRef = useRef<HTMLTableRowElement>(null);
  const centeredRef = useRef(false);
  useEffect(() => {
    const row = meRef.current;
    if (centeredRef.current || !row) return;
    centeredRef.current = true;
    const r = row.getBoundingClientRect();
    const headBottom = row.closest("table")?.querySelector("thead")?.getBoundingClientRect().bottom ?? 0;
    const body = app ? pageRef.current : row.closest<HTMLElement>(".app-modal-body");
    const phone = !app && typeof matchMedia === "function" && matchMedia("(max-width: 900px), (pointer: coarse)").matches;
    const viewBottom = phone || !body ? window.innerHeight : Math.min(window.innerHeight, body.getBoundingClientRect().bottom);
    if (r.top >= headBottom && r.bottom <= viewBottom) return;
    row.scrollIntoView({ block: "center" });
  });
  // Flipping the admin filter reloads the table, which then centres on the
  // admin's own row again like a fresh load.
  const toggleHumansOnly = () => {
    const next = !humansOnly;
    setHumansOnly(next);
    setLeaderboardHumansOnly(next);
    centeredRef.current = false;
  };


  // Dev/preview aid: ?ranksdemo overrides the real ratings so the rows walk the
  // whole ladder top-down (Wormlord at #1 → Bronze I), for judging every badge
  // in real context. Display-only; the underlying data is untouched.
  const demo = new URLSearchParams(window.location.search).has("ranksdemo");
  let top = fetched;
  if (demo && Array.isArray(fetched) && fetched.length) {
    const steps = ladderSteps();
    top = fetched.map((r, i) => ({ ...r, rating: steps[Math.max(0, steps.length - 1 - i)] }));
  }

  // The table is "loading" until the top has arrived and, for a signed-in
  // player, their own neighbourhood too (so the first paint can already be
  // centred on them instead of jumping a beat later).
  const loading = top === undefined || (!!user && around === undefined);
  let rows: Row[] | null | undefined = loading ? undefined : top;
  if (!loading && Array.isArray(top) && user && Array.isArray(around) && around.length) {
    const lastTop = top.length ? top[top.length - 1].position : 0;
    const extra = around.filter((r) => r.position > lastTop);
    const inTop = top.some((r) => r.agentId === user.id);
    if (!inTop && extra.length) {
      const skipped = extra[0].position - lastTop - 1;
      rows = skipped > 0 ? [...top, { gap: skipped }, ...extra] : [...top, ...extra];
    }
  }

  // "Top" chip in the panel header: appears once the podium has scrolled out
  // of view (the panel's own scroll area is the modal body) and jumps back up.
  // On phones the page itself scrolls (the panel is in flow there, see
  // app-responsive.scss), so the window is the scroller instead.
  const tableRef = useRef<HTMLTableElement>(null);
  const [scroller, setScroller] = useState<HTMLElement | Window | null>(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const phone = !app && typeof matchMedia === "function" && matchMedia("(max-width: 900px), (pointer: coarse)").matches;
    const el = app ? pageRef.current : phone ? null : tableRef.current?.closest<HTMLElement>(".app-modal-body") ?? null;
    const target: HTMLElement | Window | null = phone ? window : el;
    setScroller(target);
    if (!target) return;
    const onScroll = () => setScrolled((el ? el.scrollTop : window.scrollY) > 160);
    onScroll();
    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, [loading, tab, app]);
  // Always mounted once the scroller is known, so it can fade out as well as
  // in (a conditional render could only ever animate its entrance).
  // The admin's real-players switch sits at the right end of the title row,
  // with the Top chip fading in beside it. Admin-only, and the label names
  // the bots: this chrome never renders for a player.
  useModalHeadSlot(
    scroller || admin ? (
      <>
        {scroller && !app ? (
          <button
            type="button"
            className={scrolled ? `${styles.lbTopBtn} ${styles.lbTopBtnOn}` : styles.lbTopBtn}
            aria-hidden={!scrolled}
            tabIndex={scrolled ? 0 : -1}
            onClick={() => scroller.scrollTo({ top: 0, behavior: "smooth" })}
            title="Back to the top of the leaderboard"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 15 12 9 18 15" />
            </svg>
            Top
          </button>
        ) : null}
        {/* (not on the Royale tab: its list of winners is its own) */}
        {admin && !royale ? (
          <button
            type="button"
            className={humansOnly ? `${styles.lbFilterBtn} ${styles.lbFilterBtnOn}` : styles.lbFilterBtn}
            aria-pressed={humansOnly}
            onClick={toggleHumansOnly}
            title={humansOnly ? "Showing real players only. Click to show everyone, bots included" : "Admin: hide the bots and show only real players"}
          >
            <span className={styles.lbFilterDot} aria-hidden="true" />
            No bots
          </button>
        ) : null}
      </>
    ) : null,
  );

  const header = (
    <thead>
      <tr>
        <th className={styles.lbRank}>#</th>
        {/* The flag has its own narrow headerless column, left of the name, so
            names stay aligned whether or not a row has one. */}
        <th className={styles.lbFlag} aria-label="Flag" />
        <th>Player</th>
        {/* The rank insignia has its own headerless column, so badges stack
            in a line and the numbers beside them right-align like the rest. */}
        <th className={styles.lbBadge} aria-label="Rank" />
        <th className={`${styles.lbNum} ${styles.lbEloValue}`}>Elo</th>
        <th className={`${styles.lbNum} ${styles.lbGames} ${styles.lbHideSm}`}>Games</th>
        <th className={`${styles.lbNum} ${styles.lbPct}`}>
          <span
            className={styles.lbTip}
            data-tip="How much of the field this player typically outlasts: 100% means winning every round, 50% is mid-pack."
          >Place&nbsp;%</span>
        </th>
      </tr>
    </thead>
  );

  return (
    <div className={`section ${styles.lbPage}`} ref={pageRef}>
      <SectionTabs<LadderTab> tabs={LADDERS} value={tab} onChange={selectTab} />
      {/* Nothing rates on the casual ladder any more (Play Now and the Royale are
          unrated): it stays for a while as it stood, and says so. */}
      {tab === "casual" && (
        <div className={styles.lbRetiringWrap}><p className={styles.lbRetiring} role="note">Casual ratings are frozen. This tab is going away soon.</p></div>
      )}
      {royale ? (
        <RoyaleLadder tableRef={tableRef} meRef={meRef} />
      ) : rows === undefined ? (
        <table className={styles.lbTable} ref={tableRef} aria-busy="true" aria-label="Loading the leaderboard">
          {header}
          <tbody><SkeletonRows /></tbody>
        </table>
      ) : rows === null ? (
        <div className="section-placeholder">
          Couldn't load the leaderboard. Check your connection and try again in a bit.
        </div>
      ) : rows.length === 0 ? (
        <div className="section-placeholder">
          {tab === "casual"
            ? "No ranked players yet. Sign in and play an online match to claim a spot."
            : `Nobody has finished their placements in ${tab === "duel" ? "1 vs 1" : "6 FFA"} yet. Five ranked matches and you are on the ladder.`}
        </div>
      ) : (
        <table className={styles.lbTable} ref={tableRef}>
          {header}
          <tbody>
            {rows.map((r) => {
              if (isGap(r)) {
                return (
                  <tr key="gap" className={styles.lbGap} aria-label={`${r.gap} more players`}>
                    <td colSpan={7}>{r.gap.toLocaleString()} more {r.gap === 1 ? "player" : "players"}</td>
                  </tr>
                );
              }
              const isMe = !!user && r.agentId === user.id;
              const profilePath = isMe ? "/profile" : `/profile/${encodeURIComponent(r.agentId)}`;
              return (
                <tr
                  key={r.agentId}
                  className={isMe ? `${styles.lbRow} ${styles.lbMe}` : styles.lbRow}
                  ref={isMe ? meRef : undefined}
                  aria-label={isMe ? "You" : undefined}
                  // The whole row opens the profile; the name is a real link
                  // for the keyboard and for open-in-new-tab, and its own
                  // click must not navigate twice.
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a")) return;
                    navigate(profilePath);
                  }}
                >
                  <td className={styles.lbRank}>
                    {r.position <= 3 ? <span className={styles.lbMedal}>{MEDALS[r.position - 1]}</span> : r.position}
                  </td>
                  {/* No "You" chip: the framed row is the marker. Screen readers
                      still get it via the row's aria-label. */}
                  <td className={styles.lbFlag}>
                    {r.flag && isFlagCode(r.flag) ? <img className={styles.lbFlagImg} src={flagUrlSquare(r.flag)} alt="" title={flagName(r.flag)} loading="lazy" /> : null}
                  </td>
                  <td className={styles.lbName}>
                    <Link to={profilePath} className={styles.lbNameLink} title={isMe ? "Your profile" : `${r.displayName}'s profile`}>{r.displayName}</Link>
                  </td>
                  {/* OpenFront-style Elo: the insignia (tier name rides in its
                      hover title), then the exact number, right-aligned. */}
                  <td className={styles.lbBadge}><RankInsignia value={r.rating} size={22} /></td>
                  <td className={`${styles.lbNum} ${styles.lbEloValue}`}>{r.rating}</td>
                  <td className={`${styles.lbNum} ${styles.lbGames} ${styles.lbHideSm}`}>{r.games}</td>
                  {/* Placement%: the share of the field typically beaten (100%
                      wins every game at any size, 50% lands mid-field). */}
                  <td className={`${styles.lbNum} ${styles.lbPct}`}>{r.placementPct == null ? "–" : r.placementPct + "%"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
